import { createContext, useContext, useState, useCallback } from 'react';

/**
 * Notifications Context for global notification management
 */
const NotificationsContext = createContext();

/**
 * Notifications Provider - Wrap your app with this to enable global notifications
 */
export const NotificationsProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const addNotification = useCallback((message, type = 'info', duration = 3500) => {
    const id = Date.now();
    const notification = { id, message, type, duration };
    
    setNotifications(prev => [...prev, notification]);
    
    return id;
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const showError = useCallback((message, duration = 3500) => 
    addNotification(message, 'error', duration), [addNotification]);

  const showSuccess = useCallback((message, duration = 3500) => 
    addNotification(message, 'success', duration), [addNotification]);

  const showInfo = useCallback((message, duration = 3500) => 
    addNotification(message, 'info', duration), [addNotification]);

  const value = {
    notifications,
    addNotification,
    removeNotification,
    showError,
    showSuccess,
    showInfo,
  };

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};

/**
 * Hook to use notifications context
 * @returns {Object} Notifications context with helper methods
 */
export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationsProvider');
  }
  return context;
};
